import {findByFieldName} from '../../../../fields'
import {QuantitySelection} from '../../../../fields/quantity/types'
import {SLOT_MANAGEMENT_DEPENDENT_FIELDS} from '../../../../slotManagement'
import {ChangeableFields} from '../../changeableFields/types'

/**
 * Extracts the numeric quantity value from a QuantitySelection.
 * For volume mode: returns the volume value.
 * For fullLoads mode: returns the minQuantity (capacity footprint).
 */
const getNumericQuantity = (quantity: QuantitySelection | undefined): number | undefined => {
  if (!quantity) return undefined
  return quantity.type === 'volume' ? quantity.volume : quantity.minQuantity
}

/**
 * Determines whether the current form state represents a quantity-only decrease
 * on a confirmed order. When true, slot validation should be skipped and the change
 * should be auto-approved.
 *
 * Business rule (HCP-108022):
 * - Order must be confirmed (reason 01/02)
 * - Only quantity (and optionally plusLoadExpected/spacingOrFlowRate) is changed among slot-managed fields
 *   (spacingOrFlowRate is allowed because it gets auto-adjusted by useLoadSpacingAdjustment
 *   as a side effect of quantity changes in multi-truck scenarios)
 * - The quantity is strictly decreased (new < original)
 *
 * @param params.isOrderConfirmedByCustomer - Whether the order is confirmed
 * @param params.changeableFields - Current changeable field states (from orderChangeRulesOnly mode)
 * @param params.currentQuantity - Current form quantity value
 * @param params.originalQuantity - Original order quantity (from form defaultValues)
 */
export const isQuantityOnlyDecreaseOnConfirmedOrder = ({
  isOrderConfirmedByCustomer,
  changeableFields,
  currentQuantity,
  originalQuantity
}: {
  isOrderConfirmedByCustomer: boolean
  changeableFields: ChangeableFields
  currentQuantity: QuantitySelection | undefined
  originalQuantity: QuantitySelection | undefined
}): boolean => {
  // Must be a confirmed order
  if (!isOrderConfirmedByCustomer) return false

  // Check that no other slot-managed fields (besides quantity/plusLoadExpected/spacingOrFlowRate) are dirty
  // spacingOrFlowRate is allowed because useLoadSpacingAdjustment auto-adjusts it
  // as a side effect of quantity changes in multi-truck scenarios
  const otherSlotFieldsDirty = SLOT_MANAGEMENT_DEPENDENT_FIELDS.filter(
    (fieldName) =>
      fieldName !== 'quantity' &&
      fieldName !== 'plusLoadExpected' &&
      fieldName !== 'spacingOrFlowRate'
  ).some((fieldName) => {
    const field = findByFieldName(changeableFields, fieldName)
    return field?.isDirty
  })

  if (otherSlotFieldsDirty) return false

  // Quantity must be dirty
  const quantityField = findByFieldName(changeableFields, 'quantity')
  if (!quantityField?.isDirty) return false

  // Compare numeric values to detect decrease
  const originalValue = getNumericQuantity(originalQuantity)
  const currentValue = getNumericQuantity(currentQuantity)

  if (originalValue === undefined || currentValue === undefined) return false

  return currentValue < originalValue
}
